import * as plugins from './plugins.js';
import { TypedResponseError } from './classes.typedresponseerror.js';
import { TypedTools } from './classes.typedtools.js';

export type TReverseVirtualStreamDirections<T> =
  T extends plugins.typedRequestInterfaces.IVirtualStreamSender
    ? plugins.typedRequestInterfaces.IVirtualStreamReceiver
    : T extends plugins.typedRequestInterfaces.IVirtualStreamReceiver
      ? plugins.typedRequestInterfaces.IVirtualStreamSender
      : T extends (...args: infer TArguments) => infer TResult
        ? (...args: TReverseVirtualStreamDirections<TArguments>) =>
            TReverseVirtualStreamDirections<TResult>
        : T extends readonly unknown[]
          ? { [TKey in keyof T]: TReverseVirtualStreamDirections<T[TKey]> }
          : T extends object
            ? { [TKey in keyof T]: TReverseVirtualStreamDirections<T[TKey]> }
            : T;

export type THandlerFunction<T extends plugins.typedRequestInterfaces.ITypedRequest> = (
  requestArg: TReverseVirtualStreamDirections<T['request']>,
  typedToolsArg?: TypedTools
) => Promise<TReverseVirtualStreamDirections<T['response']>>;

/**
 * typed handler for dealing with typed requests
 */
export class TypedHandler<T extends plugins.typedRequestInterfaces.ITypedRequest> {
  public method: string;
  private handlerFunction: THandlerFunction<T>;
  private static readonly internalErrorMessage = 'Internal server error';

  constructor(methodArg: T['method'], handlerFunctionArg: THandlerFunction<T>) {
    this.method = methodArg;
    this.handlerFunction = handlerFunctionArg;
  }

  /**
   * adds a response to the typedRequest
   * @param typedRequestArg
   */
  public async addResponse(
    typedRequestArg: T,
    abortSignalArg?: AbortSignal,
  ) {
    if (typedRequestArg.method !== this.method) {
      throw new Error(
        'this handler has been given a wrong method to answer to. Please use a TypedRouter to filter requests'
      );
    }
    let typedResponseError: TypedResponseError | undefined;
    const typedtoolsInstance = new TypedTools(abortSignalArg);
    // Pass localData from the request to TypedTools so handlers can access transport-layer context
    if (typedRequestArg.localData) {
      typedtoolsInstance.localData = typedRequestArg.localData;
    }
    const response = await this.handlerFunction(
      typedRequestArg.request as TReverseVirtualStreamDirections<T['request']>,
      typedtoolsInstance
    ).catch((e) => {
      if (e instanceof TypedResponseError) {
        typedResponseError = e;
      } else {
        console.error(`TypedRequest handler '${this.method}' failed`);
        typedResponseError = new TypedResponseError(TypedHandler.internalErrorMessage);
      }
    });

    if (typedResponseError) {
      typedRequestArg.error = {
        text: typedResponseError.errorText,
        data: typedResponseError.errorData,
      };
    }

    if (response) {
      typedRequestArg.response = response;
    }

    typedRequestArg?.correlation?.phase ? (typedRequestArg.correlation.phase = 'response') : null;

    return typedRequestArg;
  }
}
